import React, { useCallback, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';

import BackButton from '@ts-core/components/BackButton';
import { useCourseContext } from '@ts-core/context/course';

import { Message } from '../../types';
import { useMessages, useMessageActions } from '../../hooks';

import MessageNotificationsList from '../../components/MessageNotificationsList';
import MessageDetails from '../../components/MessageDetails';

const MessagePage = () => {
  const history = useHistory();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { messageId } = useParams<{ messageId?: string }>();
  const { messages, unseen, loading, selectedMessage } = useMessages({ selectedMessageId: messageId });
  const { onMarkNotificationsAsSeen, onMarkMessageAsRead } = useMessageActions();
  const { course } = useCourseContext();
  const { _id: courseId } = course || {};

  const onNotificationClick = useCallback(
    (message: Message) => {
      history.push(message.id, { replace: true });
    },
    [history]
  );

  const handleGoBack = useCallback(() => {
    if (isSmall) {
      history.push('/messages');
    } else {
      if (courseId) {
        history.push(`${courseId}/dashboard`);
      } else {
        history.push('/courses');
      }
    }
  }, [history, isSmall, courseId]);

  useEffect(() => {
    if (unseen > 0) {
      onMarkNotificationsAsSeen();
    }
  }, [unseen, onMarkNotificationsAsSeen, isSmall]);

  useEffect(() => {
    if (selectedMessage && !selectedMessage.isRead) {
      onMarkMessageAsRead(selectedMessage);
    }
  }, [selectedMessage, onMarkMessageAsRead]);

  return (
    <Box height="calc(100vh - 160px)">
      <BackButton onGoBack={handleGoBack} title={isSmall ? 'Mensaje' : 'Mensajes'} subtitle={isSmall ? 'Mensajes' : 'Cursos'} />
      <Box display="flex" flexDirection="row" height="100%" mt={1}>
        <Box
          width="100%"
          height="100%"
          display={{ xs: 'none', md: 'flex' }}
          maxWidth={{ xs: undefined, md: 320, lg: 460 }}
          borderRight={{ xs: undefined, md: '1px solid rgba(0, 0, 0, 0.1)' }}
          pr={{ xs: 0, md: 2 }}
          sx={{ overflowY: 'scroll' }}
        >
          <MessageNotificationsList
            messages={messages}
            selectedMessage={selectedMessage}
            loading={loading}
            onNotificationClick={onNotificationClick}
          />
        </Box>
        <Box pl={{ xs: 0, md: 2 }} width="100%" height="100%" sx={{ overflowY: 'auto' }}>
          <MessageDetails message={selectedMessage} loading={loading} />
        </Box>
      </Box>
    </Box>
  );
};

export default MessagePage;
